/*
 *   File : users.js
 *   Author : https://evoqins.com
 *   Description : users component
 *   Integrations : NA
 *   Version : 1.0.0
 */
import React, { useEffect, useState, useRef } from "react";
import { PrimaryButton } from "../../Components/Buttons";
import { Filter } from "../../Components/Filters";
import { DataTableComponent } from "../../Components/DataTable";
import { useDispatch, useSelector } from "react-redux";
import {
  setPageTitle,
  setSearchEnabled,
  setPlaceHolder,
  clearSearchQuery,
} from "../../Redux/Ui/ui.slice";
import { useNavigate, useParams } from "react-router-dom";
import { Tabs } from "../../Components/Navigation";
import { Chip, EmptyScreen, Loader } from "../../Components/Other";
import { toast } from "react-toastify";
import { APIService } from "../../Service";
import {
  clearListState,
  saveListState,
  LIST_STATE_KEY,
} from "../../Redux/Filter/filterSlice";

const TAB_DATA = [
  { id: 0, label: "all-users", name: "All Users" },
  { id: 2, label: "non-invested", name: "Non Invested" },
  { id: 1, label: "invested", name: "Invested" },
];

const RISK_CONFIG = {
  "very low": {
    label: "Very Low Risk",
    bg: "bg-emerald-green-43",
    text: "text-neon-green",
  },
  low: {
    label: "Low Risk",
    bg: "bg-olive-green-43",
    text: "text-lime-olive",
  },
  moderate: {
    label: "Moderate Risk",
    bg: "bg-golden-ochre-43",
    text: "text-honey",
  },
  high: {
    label: "High Risk",
    bg: "bg-burnt-sienna-43",
    text: "text-vivid-orange",
  },
  "very high": {
    label: "Very High Risk",
    bg: "bg-soft-crimson-43",
    text: "text-vivid-red",
  },
};

// Column id -> sort_by values from user/filter-data ([ascending, descending] arrow)
const COLUMN_SORT_MAP = {
  user_id: [2, 3],
  name: [4, 5],
  joined_on: [6, 7],
  risk_status: [8, 9],
  kyc_status: [10, 11],
  user_status: [12, 13],
};

// Stable fallback so the list effect doesn't refetch on every render
const NO_FILTERS = {};

export default function Users() {
  const [loader, setLoader] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [NonInvestedfilterData, setNonInvestedfilterData] = useState([]);
  const [InvestedfilterData, setInvestedfilterData] = useState([]);
  const [sortOptions, setSortOptions] = useState([]);
  const [selectedRow, setSelectedRow] = useState();
  const [isAllSelected, setIsAllSelected] = useState(false);

  const { type } = useParams();
  const navigate = useNavigate();
  const listFetchIdRef = useRef(0);

  //Redux title and searchbar setting
  const dispatch = useDispatch();

  // Tab, page & page size live in the Redux store so returning from user detail restores them
  const listState = useSelector(
    (state) => state.persistedFilters[LIST_STATE_KEY]?.users,
  );
  const activeTabId = listState?.tab ?? 0;
  const page = listState?.page ?? 1;
  const perPage = listState?.perPage ?? 10;
  // { columnId, asc } of the active column sort arrow, or null for the API default
  const sort = listState?.sort ?? null;
  const sortBy = sort ? COLUMN_SORT_MAP[sort.columnId]?.[sort.asc ? 0 : 1] : null;
  // Filter modal selections kept per tab, so each tab has its own independent filters
  const filtersByTab = listState?.filtersByTab;
  const appliedFilters = filtersByTab?.[activeTabId] ?? NO_FILTERS;

  const _updateListParams = (next) => {
    dispatch(saveListState("users", next));
  };
  const setPage = (p) => _updateListParams({ page: p });

  useEffect(() => {
    dispatch(setPageTitle(["Users"]));
    dispatch(setSearchEnabled(true));
    dispatch(setPlaceHolder("Search with User name, Email and Phone"));
    return () => {
      listFetchIdRef.current += 1;
      dispatch(clearSearchQuery());
    };
  }, [dispatch]);

  const searchQuery = useSelector((state) => state.ui.searchQuery);

  // Deep link like /users/invested opens that tab; plain /users keeps the stored tab
  useEffect(() => {
    if (!type) return;
    const tab = TAB_DATA.find((t) => t.label === type);
    if (tab) {
      _updateListParams({ tab: tab.id, page: 1 });
    } else {
      navigate("/users", { replace: true });
    }
  }, [type]);

  useEffect(() => {
    _getFilterData();
  }, []);

  // Clear persisted filters & list state when leaving Users (except to user detail).
  // Deferred so StrictMode's simulated unmount/remount doesn't wipe them.
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      setTimeout(() => {
        if (isMountedRef.current) return;
        const goingToDetail =
          window.location.pathname.startsWith("/users/detail");
        if (!goingToDetail) {
          dispatch(clearListState("users"));
        }
      });
    };
  }, []);

  // A new search must start from page 1, otherwise results beyond page 1 look empty
  const prevSearchRef = useRef(searchQuery);
  useEffect(() => {
    if (prevSearchRef.current !== searchQuery) {
      prevSearchRef.current = searchQuery;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    _getUsers();
  }, [appliedFilters, activeTabId, searchQuery, page, perPage, sortBy]);

  // Select All covers every page of the current list, so only a different list clears it
  useEffect(() => {
    setIsAllSelected(false);
  }, [appliedFilters, activeTabId, searchQuery, perPage, sortBy]);

  // A column gets a sort arrow only when filter-data returned both of its sort_by values
  const _isSortable = (columnId) =>
    Boolean(COLUMN_SORT_MAP[columnId]) &&
    COLUMN_SORT_MAP[columnId].every((value) =>
      sortOptions.some((opt) => opt.value === value),
    );

  const COLUMNS = [
    {
      id: "user_id",
      name: "User ID",
      selector: (row) => row.customer_display_id,
      sortable: _isSortable("user_id"),
    },

    {
      id: "name",
      name: "User Name",
      wrap: true,
      sortable: _isSortable("name"),
      width: "200px",
      selector: (row) => row.name,
      cell: (row) => {
        const name = row.name || "NA";
        const initials = row.name?.trim()?.slice(0, 2)?.toUpperCase() || "NA";

        return (
          <div
            className="d-flex column-gap-2 align-items-center e-cursor-pointer e-table-link-group"
            role="link"
            tabIndex={0}
            onClick={() => _openDetail(row.customer_id)}
            onKeyDown={(e) => {
              if (e.key === "Enter") _openDetail(row.customer_id);
            }}
          >
            {row.profile_image ? (
              <img
                src={row.profile_image}
                alt={name}
                className="flex-shrink-0 rounded-circle object-fit-cover"
                style={{
                  width: "20px",
                  height: "20px",
                  minWidth: "20px",
                  aspectRatio: "1 / 1",
                }}
              />
            ) : (
              <div
                className="d-flex flex-shrink-0 align-items-center justify-content-center rounded-circle bg-secondary text-white fw-semibold"
                style={{ width: "20px", height: "20px", fontSize: "9px" }}
              >
                {initials}
              </div>
            )}

            <span className="text-facebook-blue e-table-link e-table-user-name">
              {row.name || "N/A"}
            </span>
          </div>
        );
      },
    },

    {
      id: "joined_on",
      name: "Joined On",
      sortable: _isSortable("joined_on"),
      width: "130px",
      selector: (row) => row.joined_on,
    },

    {
      id: "risk_status",
      name: "Risk Status",
      sortable: _isSortable("risk_status"),
      width: "170px",
      selector: (row) => row.risk_appetite,
      cell: (row) => {
        const key = row.risk_appetite?.trim().toLowerCase();
        const risk = RISK_CONFIG[key];

        if (!risk) return <span>N/A</span>;

        return (
          <Chip
            text={risk.label}
            style={`px-2 py-1 ${risk.bg} bg-opacity-43`}
            textClr={risk.text}
          />
        );
      },
    },

    {
      name: "Email",
      width: "200px",
      selector: (row) => row.email,
      cell: (row) => row.email || "—",
    },

    {
      name: "Phone Number",
      width: "150px",
      selector: (row) => row.phone,
    },

    {
      id: "kyc_status",
      name: "KYC Status",
      width: "150px",
      sortable: _isSortable("kyc_status"),
      selector: (row) => row.kyc_status,
      cell: (row) => (
        <p
          className={`mb-0 text-capitalize ${
            row.kyc_status === "COMPLETED"
              ? "text-medium-sea-green"
              : "text-copper-red"
          }`}
        >
          {row.kyc_status}
        </p>
      ),
    },

    {
      id: "user_status",
      name: "User Status",
      width: "150px",
      sortable: _isSortable("user_status"),
      selector: (row) => row.user_status?.toLowerCase(),
      cell: (row) => (
        <p
          className={`mb-0 text-capitalize ${
            row.user_status === "ACTIVE"
              ? "text-medium-sea-green"
              : "text-copper-red"
          }`}
        >
          {row.user_status}
        </p>
      ),
    },

    {
      name: "Action",
      width: "130px",
      cell: (row) => (
        <PrimaryButton
          ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue e-table-link"
          name="View details"
          onPress={() => _openDetail(row.customer_id)}
        />
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  //service to get filterData; sort_by drives the column sort arrows, not the Filter modal
  const _getFilterData = () => {
    APIService(false, "user/filter-data")
      .then((res) => {
        if (res?.status_code !== 200 || !Array.isArray(res.data)) return;
        const list = res.data;
        const modalFilters = list.filter((e) => e.title !== "sort_by");
        setSortOptions(list.find((e) => e.title === "sort_by")?.options || []);
        setNonInvestedfilterData(modalFilters);
        setInvestedfilterData(
          modalFilters.filter((e) => e.title !== "kyc_status"),
        );
      })
      .catch((err) => {
        console.error("Failed to load user filter data:", err);
      });
  };

  // Filter / search / sort part shared by user/list and user/list-download
  const _getListParams = () => ({
    query: searchQuery || "",
    sort_by: sortBy ?? null,
    risk_appetite: appliedFilters.risk_status || [],
    // KYC filter isn't offered on the Invested tab
    kyc_status: activeTabId !== 1 ? appliedFilters.kyc_status || [] : [],
    user_status: appliedFilters.user_status || [],
    // null = All Users, 1 = Invested, 2 = Non Invested
    investment_status: activeTabId === 0 ? null : activeTabId,
    account_creation_progress: null,
    from_date: null,
    to_date: null,
  });

  //service to get user list
  const _getUsers = () => {
    const fetchId = ++listFetchIdRef.current;
    setLoader(true);
    // Table remounts after loading, which clears its internal selection;
    // Select All survives page changes and re-selects the new page on remount
    setSelectedRow(undefined);
    // Filter/search changes already reset page to 1, so always request the current page
    const payload = {
      ..._getListParams(),
      page_num: page,
      page_size: perPage,
    };

    APIService(true, "user/list", payload)
      .then((res) => {
        if (fetchId !== listFetchIdRef.current) return;
        if (res.status_code === 200) {
          const users = res.data?.data || [];

          setTableData(users);
          // ✅ FIX: Use total_count from backend response
          setTotalRows(res.data?.total_count || 0);
          setIsEmpty(users.length === 0);
        } else {
          setTableData([]);
          setTotalRows(0);
          setIsEmpty(true);
        }
      })
      .catch(() => {
        if (fetchId !== listFetchIdRef.current) return;
        setTableData([]);
        setTotalRows(0);
        setIsEmpty(true);
      })
      .finally(() => {
        if (fetchId === listFetchIdRef.current) {
          setLoader(false);
        }
      });
  };

  // API - Download User List
  const _downloadUserList = () => {
    if (isEmpty || !selectedRow?.selectedRows?.length) return;
    setDownloadLoading(true);
    const user_ids = isAllSelected
      ? []
      : selectedRow.selectedRows.map((elem) => parseInt(elem.customer_id));
    const listParams = _getListParams();
    const params = {
      ...listParams,
      // list-download expects an array here, user/list a single integer
      investment_status:
        listParams.investment_status === null
          ? null
          : [listParams.investment_status],
      user_ids,
    };
    // list-download expects null instead of [] for empty lists
    const payload = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [
        key,
        Array.isArray(value) && value.length === 0 ? null : value,
      ]),
    );

    let is_post = true;
    let url = "user/list-download";

    APIService(is_post, url, payload)
      .then((response) => {
        if (response?.status_code === 200 && response.data?.url) {
          const link = document.createElement("a");
          link.href = response.data.url;
          link.download = `users_list_${new Date().getTime()}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success(response.message || "File downloaded successfully", {
            type: "success",
            className: "e-toast",
          });
        } else {
          toast.dismiss();
          toast.error(response?.message || "Failed to download report", {
            type: "error",
            className: "e-toast",
          });
        }
      })
      .catch((error) => {
        console.error("Error downloading user list:", error);
        toast.dismiss();
        toast.error("Failed to download report", {
          type: "error",
          className: "e-toast",
        });
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  function _onFilterApply(selectedValues) {
    _updateListParams({
      filtersByTab: { ...filtersByTab, [activeTabId]: selectedValues },
      page: 1,
    });
  }

  // Column sort arrow -> sort_by for user/list; keeps the current page (sorting doesn't change the row count).
  // Same column cycles asc -> desc -> off (sort_by: null); a new column starts at asc.
  // The table's own direction is ignored: it only toggles and carries it over across columns.
  const _onSort = (column, direction) => {
    if (!COLUMN_SORT_MAP[column?.id]) return;
    // Under StrictMode the table re-fires onSort with its restored default sort on remount.
    // A real click always flips the direction, so a call matching the saved sort is that echo.
    if (sort?.columnId === column.id && sort.asc === (direction === "asc")) {
      return;
    }
    let nextSort = null;
    if (sort?.columnId !== column.id) {
      nextSort = { columnId: column.id, asc: true };
    } else if (sort.asc) {
      nextSort = { columnId: column.id, asc: false };
    }
    _updateListParams({ sort: nextSort });
  };

  const _openDetail = (customerId) => {
    navigate(`/users/detail/${customerId}`);
  };

  const handleSelectedRowsChange = (state) => {
    setSelectedRow(state);
    setIsAllSelected(state.allSelected);
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-column row-gap-3 pt-3">
          <div className="row">
            <div className="col-lg-3 col-md-4 col-sm-5 offset-lg-9 offset-md-8 offset-sm-7">
              <div className="d-flex justify-content-end ps-3">
                <PrimaryButton
                  ClassName="e-download-btn rounded-16  bg-midnight-blue lh-24 py-2 text-white fs-16 fw-400 w-70"
                  name={downloadLoading ? "Downloading..." : "Download Report"}
                  loading={downloadLoading}
                  disabled={
                    downloadLoading ||
                    loader ||
                    isEmpty ||
                    !selectedRow?.selectedRows?.length
                  }
                  onPress={_downloadUserList}
                />
              </div>
            </div>
          </div>
          <div className="e-glass-card rounded-16 d-flex flex-column rg-12 bg-transparent">
            <div className="d-flex justify-content-between align-items-center pt-24 px-24 pb-3">
              <Tabs
                data={TAB_DATA}
                defaultActive={activeTabId}
                onChange={(tabId) => _updateListParams({ tab: tabId, page: 1 })}
              />
              {/* Remount per tab so the modal shows that tab's own selections */}
              <Filter
                key={activeTabId}
                filters={
                  activeTabId !== 1
                    ? NonInvestedfilterData
                    : InvestedfilterData
                }
                initialSelections={appliedFilters}
                onApply={_onFilterApply}
              />
            </div>
            {loader ? (
              <Loader />
            ) : isEmpty ? (
              <EmptyScreen message={"No users found"} />
            ) : (
              <DataTableComponent
                info={true}
                selectableRows={true}
                allSelected={isAllSelected}
                columns={COLUMNS}
                data={tableData}
                pagination={true}
                paginationServer={true}
                currentPage={page}
                onSelectedRowsChange={handleSelectedRowsChange}
                sortServer={true}
                onSort={_onSort}
                defaultSortFieldId={sort?.columnId}
                defaultSortAsc={sort?.asc ?? true}
                paginationTotalRows={totalRows}
                paginationPerPage={perPage}
                onChangePage={(p) => setPage(p)}
                onChangeRowsPerPage={(newPerPage) =>
                  _updateListParams({ page: 1, perPage: newPerPage })
                }
                className="e-table-basic"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}